import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Upload, FileText, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';

export default function AlleanzaSettingsModal({ open, onOpenChange }) {
  const [email, setEmail] = useState('');
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Load current settings
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelectedFile(null);
    api.get('/api/settings/alleanza')
      .then(res => {
        setEmail(res.data.recipient_email || '');
        setCurrentTemplate(res.data.template || null);
      })
      .catch(() => toast.error('Failed to load Alleanza settings'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.docx')) {
        toast.error('Only .docx files are accepted');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('recipient_email', email);
      if (selectedFile) {
        formData.append('template_file', selectedFile);
      }

      const res = await api.post('/api/settings/alleanza', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.template) {
        setCurrentTemplate(res.data.template);
        setSelectedFile(null);
      }

      toast.success('Alleanza settings saved');
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save settings';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch { return d; }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-role="heading">Alleanza (US) Payroll Settings</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground mt-2">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-5 py-3">
            {/* Email */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Payroll Recipient Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="payroll@alleanza.com"
                className="h-9"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                The email address that will receive the final payroll documents.
              </p>
            </div>

            {/* Template Upload */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Alleanza .docx Template
              </label>

              {/* Current template info */}
              {currentTemplate && !selectedFile && (
                <div className="flex items-center gap-2 p-2.5 border rounded-md bg-muted/30 mb-2">
                  <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{currentTemplate.filename}</p>
                    <p className="text-[10px] text-muted-foreground">
                      Uploaded {formatDate(currentTemplate.uploaded_at)}
                      {currentTemplate.size ? ` · ${formatSize(currentTemplate.size)}` : ''}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">
                    Active
                  </Badge>
                </div>
              )}

              {/* Selected new file */}
              {selectedFile && (
                <div className="flex items-center gap-2 p-2.5 border rounded-md border-primary/30 bg-primary/5 mb-2">
                  <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                    <p className="text-[10px] text-muted-foreground">{formatSize(selectedFile.size)} · Ready to upload</p>
                  </div>
                  <button onClick={() => { setSelectedFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="p-1 rounded hover:bg-muted text-muted-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Upload button */}
              <input
                ref={fileRef}
                type="file"
                accept=".docx"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                {currentTemplate ? 'Replace Template' : 'Upload Template'}
              </Button>
              <p className="text-[10px] text-muted-foreground mt-1">
                Only .docx files accepted. Uploading replaces the existing template.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving || loading}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
